import ProfileAvatarSelector from '@/components/avatar/ProfileAvatarSelector';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { getUserErrorMessage } from '@/lib/error-messages';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

const API_BASE_URL = `${window.location.origin}/api`;

interface PersonalIdentityCardProps {
    title?: string;
    description?: string;
    showAvatar?: boolean;
}

const PersonalIdentityCard: React.FC<PersonalIdentityCardProps> = ({
    title = 'Personal Information',
    description = 'Update your personal information',
    showAvatar = true,
}) => {
    const { user, token, refreshAuthSnapshot } = useAuth();
    const [profile, setProfile] = useState({
        firstName: user?.firstName || '',
        middleName: user?.middleName || '',
        lastName: user?.lastName || '',
        honorific: user?.honorific || '',
    });
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

    useEffect(() => {
        if (user) {
            setProfile({
                firstName: user.firstName || '',
                middleName: user.middleName || '',
                lastName: user.lastName || '',
                honorific: user.honorific || '',
            });
        }
    }, [user]);

    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            toast.error('You must be logged in to update your profile');
            return;
        }

        try {
            setIsUpdatingProfile(true);

            const response = await fetch(`${API_BASE_URL}/auth/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    first_name: profile.firstName,
                    middle_name: profile.middleName,
                    last_name: profile.lastName,
                    honorific: profile.honorific,
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    getUserErrorMessage(
                        errorData?.detail,
                        'Unable to update your profile right now. Please try again later.'
                    )
                );
            }

            await refreshAuthSnapshot();
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error(
                getUserErrorMessage(
                    error,
                    'Unable to update your profile right now. Please try again later.'
                )
            );
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    return (
        <Card>
            <form onSubmit={handleProfileSubmit}>
                <CardHeader>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {showAvatar ? <ProfileAvatarSelector /> : null}
                    <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input
                                id="firstName"
                                name="firstName"
                                value={profile.firstName}
                                onChange={handleProfileChange}
                                placeholder="Enter your first name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="middleName">Middle Name</Label>
                            <Input
                                id="middleName"
                                name="middleName"
                                value={profile.middleName}
                                onChange={handleProfileChange}
                                placeholder="Enter your middle name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input
                                id="lastName"
                                name="lastName"
                                value={profile.lastName}
                                onChange={handleProfileChange}
                                placeholder="Enter your last name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="honorific">Title / Honorific</Label>
                            <Input
                                id="honorific"
                                name="honorific"
                                value={profile.honorific}
                                onChange={handleProfileChange}
                                placeholder="Examples: PhD, MD, Esq."
                            />
                        </div>
                    </div>
                </CardContent>
                <CardFooter className="flex justify-end border-t pt-6">
                    <Button type="submit" disabled={isUpdatingProfile}>
                        {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
};

export default PersonalIdentityCard;
